
import type { Engine } from "@tsparticles/engine";

// Track initialization state
let isInitialized = false;
let initializationError: Error | null = null;
let initializationPromise: Promise<boolean> | null = null;

// Set a consistent version
const PARTICLES_VERSION = '3.8.1';

export async function initParticlesEngine(): Promise<boolean> {
  // Return true immediately if already initialized
  if (isInitialized) {
    return true;
  }

  // If already initializing, return the existing promise
  if (initializationPromise) {
    return initializationPromise;
  }

  // Create a new initialization promise
  initializationPromise = new Promise<boolean>(async (resolve, reject) => {
    try {
      // Import modules dynamically
      const [engineModule, reactModule, slimModule] = await Promise.all([
        import("@tsparticles/engine"),
        import("@tsparticles/react"),
        import("@tsparticles/slim")
      ]);
      
      const { initParticlesEngine } = reactModule;
      const { loadSlim } = slimModule;
      
      if (!initParticlesEngine || !loadSlim) {
        throw new Error("Required particle functions not found in modules");
      }
      
      await initParticlesEngine(async (engine: Engine) => {
        if (!engine) {
          throw new Error("Engine object is null or undefined");
        }
        await loadSlim(engine);
      });
      
      isInitialized = true;
      resolve(true);
    } catch (error) {
      if (error instanceof Error) {
        initializationError = error;
      } else {
        initializationError = new Error("Unknown error: " + String(error));
      }
      
      resolve(false);
    } finally {
      initializationPromise = null;
    }
  });

  try {
    return await initializationPromise;
  } catch (error) {
    return false;
  }
}

// Helper function to check if engine is already initialized
export function isParticlesEngineInitialized(): boolean {
  return isInitialized;
}

// Helper to get initialization error if any
export function getInitializationError(): Error | null {
  return initializationError;
}
